import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  TableSortLabel,
  TablePagination,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy,
  OpenInNew,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useApiClient } from '../utils/apiClient';

const TrackingIssuesReport = () => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [hoursThreshold, setHoursThreshold] = useState('24');
  const [orderBy, setOrderBy] = useState('labelCreatedAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [noTrackingCount, setNoTrackingCount] = useState(0);
  const [staleTrackingCount, setStaleTrackingCount] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState(['Label Printed']);
  const [pendingStatuses, setPendingStatuses] = useState(['Label Printed']);
  const [copiedId, setCopiedId] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [labelToCancel, setLabelToCancel] = useState(null);
  const [cancellingLabelId, setCancellingLabelId] = useState(null);

  // Available tracking statuses
  const availableStatuses = [
    'Registered',
    'Label Printed',
    'In Transit',
    'Out For Delivery',
    'Delivered',
    'Exception',
    'Return to Sender: In Transit',
    'Return to Sender: Exception',
    'Return to Sender: Out for Delivery',
    'Return to Sender: Delivered',
  ];

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  // Helper function to format date with timezone
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short', // Adds timezone abbreviation (e.g., PST, EST, UTC)
    });
  };

  // Helper function to format hours and minutes
  const formatHoursMinutes = (hours) => {
    if (hours === null || hours === undefined || isNaN(hours)) return 'N/A';
    const totalMinutes = Math.floor(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0 && m === 0) return '0m';
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const handleCopyShipmentId = async (shipmentId) => {
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopiedId(shipmentId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleOpenShipiumReport = (shipmentId) => {
    const url = `https://partner.shipium.com/veyer-legacy/delivery-reporting/shipment/${shipmentId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenTrackingLink = (trackingNumber, carrier) => {
    // Build carrier tracking URL based on carrier and tracking number
    if (!trackingNumber || !carrier) return;
    
    let trackingUrl = '';
    const trackingNum = trackingNumber.trim();
    
    // Common carrier tracking URL patterns
    if (carrier.toLowerCase().includes('fedex')) {
      trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackingNum}`;
    } else if (carrier.toLowerCase().includes('ups')) {
      trackingUrl = `https://www.ups.com/track?tracknum=${trackingNum}`;
    } else if (carrier.toLowerCase().includes('usps') || carrier.toLowerCase().includes('us postal')) {
      trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNum}`;
    } else if (carrier.toLowerCase().includes('dhl')) {
      trackingUrl = `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNum}`;
    } else {
      // Generic search as fallback
      trackingUrl = `https://www.google.com/search?q=${encodeURIComponent(carrier + ' track ' + trackingNum)}`;
    }
    
    if (trackingUrl) {
      window.open(trackingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCancelLabelClick = (row) => {
    if (!row.shipiumLabelId) {
      setError('No label ID found for this shipment');
      return;
    }
    setLabelToCancel(row);
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!labelToCancel) {
      setCancelConfirmOpen(false);
      return;
    }

    if (cancellingLabelId) {
      console.warn('Cancel already in progress, ignoring duplicate request');
      return;
    }

    const { shipiumLabelId } = labelToCancel;
    setCancelConfirmOpen(false);
    setCancellingLabelId(shipiumLabelId);
    setError(null);

    try {
      await apiClient.post('/api/v1/labels/cancel', {
        shipiumLabelId: shipiumLabelId
      }, {
        useApiToken: true
      });

      alert('Label cancelled successfully!');
      await fetchReport(); // Refresh the report
    } catch (err) {
      console.error('Error cancelling label:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel label';
      setError(`Failed to cancel label: ${errorMessage}`);
    } finally {
      setCancellingLabelId(null);
      setLabelToCancel(null);
    }
  };

  const handleCancelDismiss = () => {
    setCancelConfirmOpen(false);
    setLabelToCancel(null);
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/analytics/reports/tracking-issues', {
        params: {
          hoursThreshold: parseInt(hoursThreshold, 10),
          page: page + 1, // API uses 1-based pagination
          limit: rowsPerPage,
          statuses: selectedStatuses.join(','),
        },
      });

      if (response.data) {
        setData(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setNoTrackingCount(response.data.counts?.noTracking || 0);
        setStaleTrackingCount(response.data.counts?.staleTracking || 0);
      } else {
        // Fallback for old API format
        const sortedData = (response.data || []).sort((a, b) => {
          return new Date(b.labelCreatedAt) - new Date(a.labelCreatedAt);
        });
        setData(sortedData);
        setTotalCount(sortedData.length);
        setTotalPages(1);
        // Calculate from data for fallback
        const noTracking = sortedData.filter((d) => d.issueType === 'no_tracking').length;
        const staleTracking = sortedData.filter((d) => d.issueType === 'stale_tracking').length;
        setNoTrackingCount(noTracking);
        setStaleTrackingCount(staleTracking);
      }
    } catch (err) {
      console.error('Error fetching tracking issues report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [hoursThreshold, page, rowsPerPage, selectedStatuses]);

  // Initialize pendingStatuses when component mounts
  useEffect(() => {
    setPendingStatuses(selectedStatuses);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort the data based on current sort settings
  const sortedData = [...data].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case 'labelCreatedAt':
        aValue = new Date(a.labelCreatedAt);
        bValue = new Date(b.labelCreatedAt);
        break;
      case 'trackingNumber':
        aValue = a.trackingNumber || '';
        bValue = b.trackingNumber || '';
        break;
      case 'carrier':
        aValue = a.carrier || '';
        bValue = b.carrier || '';
        break;
      case 'trackingEventCount':
        aValue = a.trackingEventCount || 0;
        bValue = b.trackingEventCount || 0;
        break;
      case 'hoursSinceLastEvent':
        aValue = a.hoursSinceLastEvent || 0;
        bValue = b.hoursSinceLastEvent || 0;
        break;
      case 'lastTrackingStatus':
        aValue = a.lastTrackingStatus || '';
        bValue = b.lastTrackingStatus || '';
        break;
      case 'shipiumShipmentId':
        aValue = a.shipiumShipmentId || '';
        bValue = b.shipiumShipmentId || '';
        break;
      case 'shipiumLabelId':
        aValue = a.shipiumLabelId || '';
        bValue = b.shipiumLabelId || '';
        break;
      default:
        aValue = new Date(a.labelCreatedAt);
        bValue = new Date(b.labelCreatedAt);
    }

    if (orderBy === 'labelCreatedAt' || orderBy === 'hoursSinceLastEvent' || orderBy === 'trackingEventCount') {
      // For dates and numbers, compare as values
      return order === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    } else {
      // For strings, compare as strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return order === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    }
  });

  // Counts are now fetched from the backend and stored in state (noTrackingCount, staleTrackingCount)

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Hours Threshold</InputLabel>
                <Select
                  value={hoursThreshold}
                  onChange={(e) => setHoursThreshold(e.target.value)}
                  label="Hours Threshold"
                >
                  <MenuItem value={24}>24 Hours</MenuItem>
                  <MenuItem value={48}>48 Hours</MenuItem>
                  <MenuItem value={72}>72 Hours</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Tracking Status</InputLabel>
                <Select
                  multiple
                  value={pendingStatuses}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPendingStatuses(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Tracking Status" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {availableStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={pendingStatuses.indexOf(status) > -1} />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSelectedStatuses(pendingStatuses);
                    setPage(0); // Reset to first page when filter changes
                  }}
                  disabled={loading}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const defaults = ['Label Printed'];
                    setPendingStatuses(defaults);
                    setSelectedStatuses(defaults);
                    setPage(0);
                  }}
                  disabled={loading}
                >
                  Reset to Defaults
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  Shows labels with no tracking data or only one tracking event older than the selected threshold, filtered by the selected tracking statuses
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Total Issues
                  </Typography>
                  <Typography variant="h4">{formatNumber(totalCount)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                    Showing {formatNumber(data.length)} of {formatNumber(totalCount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography color="textSecondary" gutterBottom>
                    No Tracking Data
                  </Typography>
                  <Typography variant="h4" color="error.main" sx={{ flexGrow: 1 }}>
                    {formatNumber(noTrackingCount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden' }}>
                    Placeholder
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Stale Tracking
                  </Typography>
                  <Typography variant="h4" color="warning.main" sx={{ flexGrow: 1 }}>
                    {formatNumber(staleTrackingCount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden' }}>
                    Placeholder
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Data Table */}
          <Card>
            <CardHeader title="Labels with Tracking Issues" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'labelCreatedAt'}
                          direction={orderBy === 'labelCreatedAt' ? order : 'desc'}
                          onClick={() => handleSort('labelCreatedAt')}
                        >
                          Label Created
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'shipiumShipmentId'}
                          direction={orderBy === 'shipiumShipmentId' ? order : 'desc'}
                          onClick={() => handleSort('shipiumShipmentId')}
                        >
                          Shipium Shipment ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'shipiumLabelId'}
                          direction={orderBy === 'shipiumLabelId' ? order : 'desc'}
                          onClick={() => handleSort('shipiumLabelId')}
                        >
                          Shipium Label ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'trackingNumber'}
                          direction={orderBy === 'trackingNumber' ? order : 'desc'}
                          onClick={() => handleSort('trackingNumber')}
                        >
                          Tracking Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'carrier'}
                          direction={orderBy === 'carrier' ? order : 'desc'}
                          onClick={() => handleSort('carrier')}
                        >
                          Carrier
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'trackingEventCount'}
                          direction={orderBy === 'trackingEventCount' ? order : 'desc'}
                          onClick={() => handleSort('trackingEventCount')}
                        >
                          Tracking Events
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'lastTrackingStatus'}
                          direction={orderBy === 'lastTrackingStatus' ? order : 'desc'}
                          onClick={() => handleSort('lastTrackingStatus')}
                        >
                          Last Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        Last Tracking Event
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'hoursSinceLastEvent'}
                          direction={orderBy === 'hoursSinceLastEvent' ? order : 'desc'}
                          onClick={() => handleSort('hoursSinceLastEvent')}
                        >
                          Time Since Last Event
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Issue Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="textSecondary">
                            No labels with tracking issues found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedData.map((row) => (
                        <TableRow key={row.labelId}>
                          <TableCell>
                            {formatDate(row.labelCreatedAt)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {row.shipiumShipmentId || 'N/A'}
                              </Typography>
                              {row.shipiumShipmentId && (
                                <>
                                  <Tooltip title={copiedId === row.shipiumShipmentId ? 'Copied!' : 'Copy Shipment ID'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyShipmentId(row.shipiumShipmentId)}
                                      sx={{ 
                                        opacity: 0.7,
                                        '&:hover': { opacity: 1 },
                                        color: copiedId === row.shipiumShipmentId ? 'success.main' : 'inherit'
                                      }}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Open in Shipium">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenShipiumReport(row.shipiumShipmentId)}
                                      sx={{ 
                                        opacity: 0.7,
                                        '&:hover': { opacity: 1 }
                                      }}
                                    >
                                      <OpenInNew fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {row.shipiumLabelId || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {row.trackingNumber || 'N/A'}
                              </Typography>
                              {row.trackingNumber && row.carrier && (
                                <Tooltip title="Open Carrier Tracking">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenTrackingLink(row.trackingNumber, row.carrier)}
                                    sx={{ 
                                      opacity: 0.7,
                                      '&:hover': { opacity: 1 }
                                    }}
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{row.carrier}</TableCell>
                          <TableCell align="right">
                            {formatNumber(row.trackingEventCount)}
                          </TableCell>
                          <TableCell>
                            {row.lastTrackingStatus || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {formatDate(row.lastTrackingEvent)}
                          </TableCell>
                          <TableCell align="right">
                            {formatHoursMinutes(row.hoursSinceLastEvent)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                icon={row.issueType === 'no_tracking' ? <WarningIcon /> : <CheckCircleIcon />}
                                label={row.issueType === 'no_tracking' ? 'No Tracking' : 'Stale Tracking'}
                                color={row.issueType === 'no_tracking' ? 'error' : 'warning'}
                                size="small"
                              />
                              {row.shipiumLabelId && (
                                <Tooltip title="Cancel Label">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCancelLabelClick(row)}
                                    disabled={cancellingLabelId === row.shipiumLabelId}
                                    sx={{ 
                                      opacity: 0.7,
                                      '&:hover': { opacity: 1 },
                                      color: 'error.main'
                                    }}
                                  >
                                    {cancellingLabelId === row.shipiumLabelId ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <CancelIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 250]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? formatNumber(count) : `more than ${formatNumber(to)}`}`
                }
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCancelDismiss}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancel Label?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this label? This action cannot be undone.
            {labelToCancel && (
              <>
                <br /><br />
                <strong>Shipment ID:</strong> {labelToCancel.shipiumShipmentId}<br />
                {labelToCancel.trackingNumber && (
                  <>
                    <strong>Tracking Number:</strong> {labelToCancel.trackingNumber}<br />
                  </>
                )}
                {labelToCancel.shipiumLabelId && (
                  <>
                    <strong>Label ID:</strong> {labelToCancel.shipiumLabelId}
                  </>
                )}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDismiss} disabled={!!cancellingLabelId}>
            No, Keep Label
          </Button>
          <Button 
            onClick={handleCancelConfirm} 
            color="error" 
            variant="contained"
            disabled={!!cancellingLabelId}
          >
            {cancellingLabelId ? 'Cancelling...' : 'Yes, Cancel Label'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrackingIssuesReport;

